import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Anchor, Box, Paper, Input, Loader, Image, rem, Text, HoverCard } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import axios from "axios";
import { IconSearch, IconCheck } from "@tabler/icons-react";
import { usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";

export const STOCK_PHOTO_MIME = "application/x-smads-stock-photo";

export const trackUnsplashDownload = (photo, unsplashKey) => {
  try {
    const downloadLocation = photo?.links?.download_location;
    if (!downloadLocation || !unsplashKey) return;
    axios
      .get(downloadLocation, {
        headers: { Authorization: `Client-ID ${unsplashKey}` },
      })
      .catch(() => {
      });
  } catch (err) {
    console.warn("Unsplash download track failed:", err);
  }
};

const StockPhotoTab = ({ onSelect, activeValue = "", query = "", onQueryChange, lang, draggableTiles = false }) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { unsplashKey, appName, unsplashUtmSource } = usePage().props;
  const [localQuery, setLocalQuery] = useState(query || "");

  const effectiveQuery = useMemo(
    () => (onQueryChange ? (query ?? "") : localQuery),
    [onQueryChange, query, localQuery]
  );

  const utmSource = useMemo(
    () => String(unsplashUtmSource || appName || "your_app_name").replace(/\s+/g, "_"),
    [unsplashUtmSource, appName]
  );

  const buildProfileLink = useCallback(
    (photo) => {
      const base = photo?.user?.links?.html || `https://unsplash.com/@${photo?.user?.username}`;
      const sep = base?.includes("?") ? "&" : "?";
      return `${base}${sep}utm_source=${encodeURIComponent(utmSource)}&utm_medium=referral`;
    },
    [utmSource]
  );

  const buildUnsplashLink = useCallback(() => {
    return `https://unsplash.com/?utm_source=${encodeURIComponent(utmSource)}&utm_medium=referral`;
  }, [utmSource]);

  useEffect(() => {
    if (onQueryChange) setLocalQuery(query || "");
  }, [onQueryChange, query]);

  const [debouncedQuery] = useDebouncedValue(effectiveQuery, 300);

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  function stripQuery(u) {
    if (!u || typeof u !== "string") return "";
    try {
      const url = new URL(u);
      return `${url.origin}${url.pathname}`;
    } catch {
      const i = u.indexOf("?");
      return i >= 0 ? u.slice(0, i) : u;
    }
  }

  const selectedUrl = typeof activeValue === "string" ? stripQuery(activeValue) : "";

  const fetchPhotos = async (search = "nature", pageNumber = 1) => {
    if (!unsplashKey) return;
    if (!search.trim()) return;
    if (loading || isFetchingMore) return;

    if (pageNumber === 1) setLoading(true);
    else setIsFetchingMore(true);

    try {
      const params = {
        query: search,
        page: pageNumber,
        per_page: 30,
      };

      if (lang && typeof lang === "string") {
        params.lang = lang.slice(0, 2).toLowerCase();
      }

      const res = await axios.get("https://api.unsplash.com/search/photos", {
        params,
        headers: {
          Authorization: `Client-ID ${unsplashKey}`,
          ...(params.lang ? { "Accept-Language": params.lang } : {}),
        },
      });

      const results = res.data?.results ?? [];
      setPhotos(pageNumber === 1 ? results : (prev) => [...prev, ...results]);
      setHasMore(results.length === 30);
      setPage(pageNumber);
    } catch (error) {
      console.error("Error fetching photos:", error);
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
    }
  };

  const handleIntersection = useCallback(
    (entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !loading && !isFetchingMore) {
        fetchPhotos((debouncedQuery || "nature").trim(), page + 1);
      }
    },
    [debouncedQuery, page, hasMore, loading, isFetchingMore]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    });

    if (sentinelRef.current) observer.observe(sentinelRef.current);
    observerRef.current = observer;
    return () => observerRef.current?.disconnect();
  }, [handleIntersection]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setPhotos([]);
    fetchPhotos((debouncedQuery || "nature").trim(), 1);
  }, [debouncedQuery, unsplashKey]);

  const triggerUnsplashDownload = (photo) => trackUnsplashDownload(photo, unsplashKey);

  const handleSelect = (photo) => {
    setSelected(photo.id);
    triggerUnsplashDownload(photo);
    onSelect(photo);
  };

  const handleInputChange = (e) => {
    const v = e.currentTarget.value;
    if (onQueryChange) onQueryChange(v);
    else setLocalQuery(v);
  };

  return (
    <>
      <Paper display="flex" px={12} py={5} radius="md" withBorder mb={10} style={{ alignItems: "center" }}>
        <IconSearch size={14} color="var(--mantine-color-gray-5)" />
        <Input
          variant="unstyled"
          placeholder={t('stock.searchPlaceholder')}
          value={effectiveQuery}
          onChange={handleInputChange}
          ml={12}
          radius={0}
          styles={{
            input: {
              fontSize: 12,
              padding: 0,
            },
            wrapper: {
              height: 18,
              lineHeight: "18px",
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </Paper>

      {!unsplashKey && (
        <Text c="red" fz="xs" mb="xs">
          {t('stock.noApiKey')}
        </Text>
      )}

      {loading ? (
        <Loader />
      ) : (
        <Box style={{ height: rem(400), overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          <Box style={{ columnCount: 2, columnGap: rem(6) }}>
            {photos.map((photo, idx) => {
              const pick =
                photo?.urls?.raw ||
                photo?.urls?.full ||
                photo?.urls?.regular ||
                photo?.urls?.small;

              const clean = pick?.split("?")[0];
              const isActive = clean === activeValue;

              return (
                <Box
                  key={`${photo.id}-${idx}`}
                  pos="relative"
                  onClick={() => handleSelect(photo)}
                  draggable={draggableTiles || undefined}
                  onDragStart={draggableTiles ? (e) => {
                    e.dataTransfer.setData(STOCK_PHOTO_MIME, JSON.stringify(photo));
                    e.dataTransfer.effectAllowed = "copy";
                  } : undefined}
                  style={{
                    breakInside: "avoid",
                    cursor: "pointer",
                    borderRadius: rem(10),
                    overflow: "hidden",
                    border: `2px solid ${isActive ? "var(--mantine-color-smartico-pink-5)" : "transparent"}`,
                    transition: "border 0.2s ease",
                    marginBottom: rem(6),
                  }}
                >
                  <HoverCard
                    offset={-20}
                    withinPortal={false}
                    position="bottom"
                    width="target"
                    middlewares={{ flip: false, shift: true, inline: true }}
                    openDelay={80}
                    closeDelay={100}
                    transitionProps={{
                      transition: "fade",
                      duration: 150,
                      timingFunction: "ease",
                    }}
                  >
                    <HoverCard.Target>
                      <Image
                        src={photo.urls.small}
                        alt={photo.alt_description || `Photo by ${photo?.user?.name || photo?.user?.username}`}
                        w="100%"
                        radius="md"
                        style={{ display: "block" }}
                      />
                    </HoverCard.Target>

                    <HoverCard.Dropdown
                      p={4}
                      radius={0}
                      style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        opacity: 0.8,
                        marginLeft: -5,
                        textWrap: "nowrap",
                        fontSize: 8,
                        lineHeight: 1.5,
                      }}
                    >
                      <span>{t('stock.photoBy')}</span>
                      <Anchor
                        style={{ fontSize: "inherit" }}
                        href={buildProfileLink(photo)}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        onMouseDown={(e) => e.stopPropagation()} 
                      >
                        {photo?.user?.name || photo?.user?.username || "Creator"}
                      </Anchor>
                      <span>on</span>
                      <Anchor
                        style={{ fontSize: "inherit" }}
                        href={buildUnsplashLink()}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        Unsplash
                      </Anchor>
                    </HoverCard.Dropdown>
                  </HoverCard>

                  {isActive && (
                    <Box
                      style={{
                        position: "absolute",
                        top: rem(8),
                        right: rem(8),
                        backgroundColor: "var(--mantine-color-smartico-pink-5)",
                        color: "white",
                        width: rem(28),
                        height: rem(28),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 0,
                      }}
                    >
                      <IconCheck size={16} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          <div ref={sentinelRef} style={{ height: rem(1) }} />

          {isFetchingMore && (
            <Box mt={rem(8)} mb={rem(8)} style={{ textAlign: "center" }}>
              <Loader size="sm" />
            </Box>
          )}
        </Box>
      )}

      <Box c="dimmed" fz="xs" mt={10}>
        {t('stock.licenseIntro')}{" "}
        <Anchor fz="xs" href="https://unsplash.com/license" target="_blank" rel="noopener noreferrer">
          {t('stock.licenses')}
        </Anchor>{" "}
        {t('stock.and')}{" "}
        <Anchor fz="xs" href="https://unsplash.com/terms" target="_blank" rel="noopener noreferrer">
          {t('stock.terms')}
        </Anchor>.
      </Box>
    </>
  );
};

export default StockPhotoTab;
